import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent } from 'react';

import { OpenFundRequestDocumentSlot } from '@/models/common/OpenFundRequestDocumentSlot';
import { OpenFundRequestPersonRole } from '@/models/common/OpenFundRequestPersonRole';
import { queryClient } from '@/hooks/QueryClient';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import CustomDialog from '@/components/ui/Dialog/Dialog';

import {
  boxFromDrag,
  boxToPixels,
  cropInstructions,
  describeCrop,
  isMeaningfulBox,
  survivingPageCount,
  type CropBox,
  type CropPageInstruction,
  type CropPageState,
  type Point,
  type Size,
} from './cropGeometry';
import styles from './OpenFundRequestViewPage.module.scss';

interface IdDocumentCrop {
  documentName: string;
  pageCount: number;
}

export interface CropTarget {
  personRole: OpenFundRequestPersonRole;
  personIndex: number;
  documentSlot: OpenFundRequestDocumentSlot;
  /** What the file is, in the words the page uses for it - e.g. 'צילום תעודת זהות של רכז 2'. */
  title: string;
}

interface CropIdDocumentDialogProps {
  requestId: number;
  target: CropTarget;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Cutting the ID appendix out of a copy that carries both (HAT-1302).
 *
 * The applicant has to supply the ID card and the appendix separately, so the one photograph of both that
 * they often have gets uploaded into each field. Deleting the appendix slot destroys that copy; the one
 * inside the ID-card file it cannot touch, because the ID card has to stay. Here the office marks what the
 * ID-card file keeps, and the rest stops existing.
 *
 * The office marks what SURVIVES, not what goes - the opposite of the redaction editor next door. That is
 * the way a crop is read everywhere else, and on a page where most of what is visible is being destroyed
 * it is also the safer question to answer.
 *
 * The page is shown as an image the server rendered rather than as the file itself, for the same reason the
 * redaction screen does it: while the office is deciding what to destroy, it should not also be handed an
 * address it could save the uncut file from.
 */
const CropIdDocumentDialog = ({ requestId, target, onClose, onSaved }: CropIdDocumentDialogProps) => {
  const baseUrl =
    `funds/creation/${requestId}/id-crop/${target.personRole}/${target.personIndex}/${target.documentSlot}`;

  const [page, setPage] = useState(0);
  const [pages, setPages] = useState<Record<number, CropPageState>>({});
  const [drag, setDrag] = useState<{ start: Point; current: Point } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const pageRef = useRef<HTMLDivElement>(null);

  const { data: document, isLoading } = useGetAuthApi<IdDocumentCrop>(baseUrl);
  const { data: pageImage, isLoading: isLoadingPage } = useGetAuthApi<Blob>(`${baseUrl}/pages/${page}`, 'blob');
  const { mutateAsync: crop } = usePostAuthApi<void, { pages: CropPageInstruction[] }>(baseUrl);

  const imageUrl = useMemo(() => (pageImage ? URL.createObjectURL(pageImage) : null), [pageImage]);
  useEffect(() => () => { if (imageUrl) URL.revokeObjectURL(imageUrl); }, [imageUrl]);

  const pageCount = document?.pageCount ?? 1;
  const state = pages[page] ?? {};
  const keep = state.keep;
  const isRemoved = !!state.remove;

  /**
   * The displayed size of the page, kept in state rather than read while rendering: the box is drawn from
   * it, and a size read before the browser has laid the picture out - or one that is now stale because the
   * window was resized - puts it in the wrong place.
   */
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = pageRef.current;
    if (!element) return;

    const measure = () => {
      const rect = element.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [imageUrl]);

  const pointIn = (event: { clientX: number; clientY: number }): Point => {
    const rect = pageRef.current?.getBoundingClientRect();
    return { x: event.clientX - (rect?.left ?? 0), y: event.clientY - (rect?.top ?? 0) };
  };

  const setPageState = (next: CropPageState) => setPages((current) => ({ ...current, [page]: next }));

  const onPointerDown = (event: PointerEvent) => {
    if (isRemoved || isSaving) return;
    event.preventDefault();
    pageRef.current?.setPointerCapture(event.pointerId);
    const point = pointIn(event);
    setDrag({ start: point, current: point });
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!drag) return;
    setDrag({ ...drag, current: pointIn(event) });
  };

  const onPointerUp = () => {
    if (!drag) return;

    const box = boxFromDrag(drag.start, drag.current, size);
    setDrag(null);

    // A drag of three pixels is a click that slipped, not a rectangle - and here it would be a rectangle
    // that keeps almost nothing of the card.
    if (isMeaningfulBox(box)) {
      setPageState({ keep: box });
      setError(null);
    }
  };

  /** One rectangle per page: marking again replaces it rather than adding beside it. */
  const clearKeep = () => setPageState({ ...state, keep: undefined });

  /** The rectangle is remembered while the page is marked to go, so changing one's mind costs nothing. */
  const toggleRemoved = () => {
    setError(null);
    setPageState({ remove: !isRemoved, keep: state.keep });
  };

  const instructions = useMemo(() => cropInstructions(pages), [pages]);
  const surviving = useMemo(() => survivingPageCount(pages, pageCount), [pages, pageCount]);
  const summary = useMemo(() => describeCrop(pages, pageCount), [pages, pageCount]);

  const save = useCallback(async () => {
    setError(null);
    setIsSaving(true);

    try {
      await crop({ pages: instructions });

      // Drop this document's cached pages, not just the request behind the screen. The pictures held here
      // are of the file as it was before the cut, and opening the editor again on them would have the
      // office marking a rectangle against a page that no longer exists - cutting the wrong part of the
      // real one. The server already refuses to cache them; this is the same refusal on the near side.
      queryClient.removeQueries({
        predicate: (query) => String(query.queryKey[0] ?? '').startsWith(baseUrl),
      });

      onSaved();
      onClose();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const status = e?.response?.status ?? e?.status;
      const data = e?.response?.data;
      const message = data?.message ?? (typeof data === 'string' ? data : null) ?? e?.message ?? 'שגיאה לא ידועה';
      setError(status ? `שגיאה ${status}: ${message}` : message);
      setIsConfirming(false);
    } finally {
      setIsSaving(false);
    }
  }, [crop, instructions, baseUrl, onSaved, onClose]);

  const dragBox = drag ? boxFromDrag(drag.start, drag.current, size) : null;
  const shown: CropBox | null = dragBox ?? keep ?? null;

  return (
    <CustomDialog open setOpen={(value) => { if (!value) onClose(); }} hideActionButton>
      <div className={styles.redactDialog}>
        <h3 className="heading-h4-semibold mb-1">חיתוך צילום תעודת הזהות</h3>
        <p className="body-md-regular mb-2">{target.title}</p>

        <p className="body-sm-regular mb-3">
          יש לסמן בגרירה את החלק שנשאר בקובץ — תעודת הזהות. כל מה שמחוץ לסימון, ובכלל זה הספח, נחתך
          מהקובץ והמקור נמחק. לא ניתן לבטל את הפעולה.
        </p>

        {isLoading && <div className="body-sm-regular">טוען את המסמך…</div>}

        {document && (
          <>
            <div
              ref={pageRef}
              className={isRemoved ? styles.redactPage : `${styles.redactPage} ${styles.redactPageDrawable}`}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={() => setDrag(null)}
            >
              {imageUrl
                ? (
                  <img
                    src={imageUrl}
                    alt={document.documentName}
                    className={`${styles.redactImage} ${isRemoved ? styles.cropPageRemoved : ''}`}
                    draggable={false}
                  />
                )
                : <div className={styles.redactPlaceholder}>{isLoadingPage ? 'טוען את העמוד…' : 'העמוד אינו זמין'}</div>}

              {/* The kept rectangle is a window: everything around it is dimmed, so what is being thrown
                  away is what the office sees greyed out rather than something it has to picture. */}
              {!isRemoved && shown && (
                <div className={styles.cropKeep} style={boxToPixels(shown, size)}>
                  {keep && !drag && (
                    <span className={styles.redactBoxActions}>
                      <button
                        type="button"
                        title="ביטול הסימון"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={clearKeep}
                      >
                        ×
                      </button>
                    </span>
                  )}
                </div>
              )}

              {isRemoved && <div className={styles.cropRemovedBanner}>העמוד יוסר מהקובץ</div>}
            </div>

            {pageCount > 1 && (
              <div className={styles.redactPager}>
                <Button variant="white" onClick={() => setPage(page - 1)} disabled={page === 0 || isSaving}>
                  הקודם
                </Button>
                <span className="body-sm-regular">
                  עמוד {page + 1} מתוך {pageCount}
                </span>
                <Button
                  variant="white"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pageCount - 1 || isSaving}
                >
                  הבא
                </Button>
                {/* The appendix on a page of its own - the case a rectangle cannot answer. */}
                <Button variant="white" onClick={toggleRemoved} disabled={isSaving}>
                  {isRemoved ? 'החזרת העמוד' : 'הסרת העמוד כולו'}
                </Button>
              </div>
            )}
          </>
        )}

        {error && <div className={styles.errorText}>{error}</div>}

        {surviving === 0 && (
          <div className={styles.errorText}>
            לא ניתן להסיר את כל עמודי המסמך — צילום תעודת הזהות נשמר בתיק הפרויקט.
          </div>
        )}

        {isConfirming && (
          <div className={styles.redactConfirm}>
            {summary}. מה שנחתך יימחק לצמיתות, גם מהפרויקט וגם מהאחסון, ולא ניתן יהיה לשחזר אותו. להמשיך?
          </div>
        )}

        <div className={styles.actionsBar}>
          {!isConfirming && (
            <Button
              variant="primary"
              onClick={() => { setError(null); setIsConfirming(true); }}
              disabled={instructions.length === 0 || surviving === 0 || isSaving}
            >
              חיתוך
            </Button>
          )}
          {isConfirming && (
            <Button variant="primary" onClick={save} disabled={isSaving}>
              {isSaving ? 'מבצע חיתוך…' : 'אישור החיתוך'}
            </Button>
          )}
          <Button variant="white" onClick={isConfirming ? () => setIsConfirming(false) : onClose} disabled={isSaving}>
            {isConfirming ? 'חזרה לסימון' : 'סגירה'}
          </Button>
        </div>
      </div>
    </CustomDialog>
  );
};

export default CropIdDocumentDialog;
